/**
 * Parses narrator message content into typed segments: narration prose, NPC dialogue,
 * and action/stage-direction lines.
 *
 * Supported dialogue patterns:
 *   **Name:** "dialogue"
 *   **Name** says, "dialogue"
 *   **Name** replied, "dialogue"
 *   Name: "dialogue"        (at start of line)
 *
 * Action patterns (between dialogue):
 *   *She crosses her arms.*
 *   _He draws his sword._
 *
 * Emotion cues parsed from parentheticals:
 *   **Name:** (angrily) "text"
 *   **Name** (whispering): "text"
 */

import { sanitizeLeakedToolCalls } from "./sanitize-narrator";

export interface NarratorSegment {
  type: "narration" | "dialogue" | "action" | "inner_voice" | "note";
  text: string;
  /** Only present for dialogue / action segments */
  speaker?: string;
  /** Parsed emotion cue for dialogue segments */
  emotion?: string;
  /** Optional title for note segments (e.g. header line before list) */
  noteTitle?: string;
}

// Matches bold-name dialogue patterns:
// **Name:** "text"  |  **Name** verb, "text"  |  **Name** verb: "text"
// Also captures optional parenthetical emotion cues: **Name:** (angrily) "text"
const DIALOGUE_RE =
  /\*\*([^*\n,.;:!?]{1,40}?):?\*\*(?:\s*\*\*[^*\n,.;:!?]{1,40}?:?\*\*)*(?:\s*(?:says?|said|replies?|replied|asks?|asked|whispers?|whispered|shouts?|shouted|mutters?|muttered|exclaims?|exclaimed|growls?|growled|hisses?|hissed|laughs?|laughed|sighs?|sighed|calls?|called|adds?|added|continues?|continued|began?|begins?|announces?|announced|responds?|responded|demands?|demanded|pleads?|pleaded|warns?|warned|interrupts?|interrupted|offers?|offered|suggests?|suggested|agrees?|agreed|nods?|nodded|grins?|grinned|smiles?|smiled|frowns?|frowned)\s*[,:]?\s*|\s*:?\s+)(?:\(([^)]+)\)\s*)?(["""\u201C])([\s\S]*?)(["""\u201D])/g;

// Matches bold-name dialogue WITHOUT quotes — plain text after **Name:** or **Name:** (emotion)
// Captures until end of paragraph (double newline) or end of string
const DIALOGUE_UNQUOTED_RE =
  /\*\*([^*\n,.;:!?]{1,40}?):?\*\*(?:\s*\*\*[^*\n,.;:!?]{1,40}?:?\*\*)*\s*:?\s*(?:\(([^)]+)\)\s*)?(?!["""\u201C])([A-Z\u0590-\u05FF\u0600-\u06FF][\s\S]*?)(?=\n\n|\n\*\*[^*\n,.;:!?]{1,40}\*\*|\n\*[^*]|$)/g;

// Simpler pattern for unbolded Name: "text" at line start
const SIMPLE_DIALOGUE_RE =
  /^([A-Z][a-zA-Z''\- ]{1,30}):\s*(?:\(([^)]+)\)\s*)?(["""\u201C])([\s\S]*?)(["""\u201D])/gm;

// Action/stage-direction lines: standalone italic text (*action text*)
// Must be on its own line (after newline or at start), not inline bold
const ACTION_RE = /(?:^|\n)\s*\*([^*\n][^*]{2,}[^*\n])\*\s*(?:\n|$)/g;

// Inner voice blocks: [INNER_VOICE]...[/INNER_VOICE]
const INNER_VOICE_RE = /\[INNER_VOICE\]([\s\S]*?)\[\/INNER_VOICE\]/g;

// System / meta speakers that should NOT be treated as NPC dialogue
const SYSTEM_SPEAKERS = new Set([
  "game master", "gm", "narrator", "system", "dungeon master", "dm",
  "storyteller", "director", "referee", "admin", "note", "ooc",
  "out of character", "author", "editor",
]);

/** Check if a speaker name is a system/meta speaker rather than an NPC */
function isSystemSpeaker(name: string): boolean {
  return SYSTEM_SPEAKERS.has(name.toLowerCase().trim());
}

interface RawMatch {
  start: number;
  end: number;
  type: "dialogue" | "action" | "inner_voice";
  speaker?: string;
  text: string;
  emotion?: string;
}

export function parseNarratorSegments(rawContent: string): NarratorSegment[] {
  let content = sanitizeLeakedToolCalls(rawContent);

  // Pre-pass: strip leaked [Atmosphere] / [Ambience] / [Scene] tags (e.g. "— [Atmosphere] ... —")
  content = content.replace(/—\s*\[(?:Atmosphere|Ambience|Scene|Weather|Environment)\]\s*[^—\n]+\s*—/g, "");
  // Also strip standalone bracketed tags on their own line
  content = content.replace(/^\s*\[(?:Atmosphere|Ambience|Scene|Weather|Environment)\]\s*[^\n]*$/gm, "");
  // Pre-pass: de-duplicate bold speaker names (e.g. **Elder Yaakov:** **Elder Yaakov:** → **Elder Yaakov:**)
  content = content.replace(/\*\*([^*]+?):?\*\*\s*\*\*\1:?\*\*/gi, "**$1:**");

  // Pre-pass: heal unclosed bold spans that span sentences (e.g. "**Bakta, the driver, sits...")
  // A "speaker bold" must be short and free of newlines/commas/periods. If we find an opener
  // **Word that is followed by punctuation/newline before the closing **, treat it as a stray
  // marker and drop the opening ** so the bold doesn't swallow paragraphs into a fake speaker.
  content = content.replace(/\*\*([A-Za-z\u0590-\u05FF\u0600-\u06FF][^*\n]{0,200})/g, (full, body: string) => {
    // If body contains a closing ** within a "name-shaped" window, leave it alone — real bold.
    const closeIdx = body.indexOf("**");
    if (closeIdx >= 0) {
      const inside = body.slice(0, closeIdx);
      if (inside.length <= 40 && !/[,.;:!?\n]/.test(inside)) return full;
      // Otherwise: bold span spans sentence-level punctuation — strip both markers.
      return body.slice(0, closeIdx) + body.slice(closeIdx + 2);
    }
    // No closer at all in the next 200 chars — drop the opener.
    return body;
  });

  // Pre-pass: extract [Offscreen] lines as fallback (should be in metadata, but strip if leaked)
  const offscreenLines: string[] = [];
  content = content.replace(/\*?\[Offscreen\]\s*([^*\n]+)\*?\s*\n?/g, (_match, body) => {
    offscreenLines.push(body.trim());
    return "";
  });
  if (!content?.trim()) return [{ type: "narration", text: "" }];

  // Extract inner voice blocks first (highest priority)
  const innerVoiceMatches: RawMatch[] = [];
  INNER_VOICE_RE.lastIndex = 0;
  let ivm: RegExpExecArray | null;
  while ((ivm = INNER_VOICE_RE.exec(content)) !== null) {
    innerVoiceMatches.push({
      start: ivm.index,
      end: ivm.index + ivm[0].length,
      type: "inner_voice",
      text: ivm[1].trim(),
    });
  }

  const matches: RawMatch[] = [...innerVoiceMatches];

  // Helper to check overlap with existing matches (including inner_voice)
  const overlapsAny = (start: number, end: number) =>
    matches.some((ex) => (start >= ex.start && start < ex.end) || (end > ex.start && end <= ex.end));

  // Helper to detect if a match position is inside a bullet/list line
  const isOnListLine = (pos: number): boolean => {
    const lineStart = content.lastIndexOf("\n", pos) + 1;
    const linePrefix = content.slice(lineStart, pos);
    return /^\s*[-•*→▸▹▶]\s+/.test(linePrefix);
  };

  // Bold-name dialogue patterns
  let m: RegExpExecArray | null;
  DIALOGUE_RE.lastIndex = 0;
  while ((m = DIALOGUE_RE.exec(content)) !== null) {
    if (!overlapsAny(m.index, m.index + m[0].length) && !isOnListLine(m.index) && !isSystemSpeaker(m[1])) {
      matches.push({
        start: m.index,
        end: m.index + m[0].length,
        type: "dialogue",
        speaker: m[1].trim(),
        emotion: m[2]?.trim() || undefined,
        text: m[4], // the dialogue text inside quotes
      });
    }
  }

  // Simple Name: "text" patterns (only add if no overlap with bold matches)
  SIMPLE_DIALOGUE_RE.lastIndex = 0;
  while ((m = SIMPLE_DIALOGUE_RE.exec(content)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (!overlapsAny(start, end) && !isSystemSpeaker(m[1])) {
      matches.push({
        start,
        end,
        type: "dialogue",
        speaker: m[1].trim(),
        emotion: m[2]?.trim() || undefined,
        text: m[4],
      });
    }
  }

  // Unquoted bold-name dialogue: **Name:** text without quotes
  // Skip lines that look like list items to avoid treating
  // **Item Name:** description as dialogue in inventory/equipment lists.
  DIALOGUE_UNQUOTED_RE.lastIndex = 0;
  while ((m = DIALOGUE_UNQUOTED_RE.exec(content)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (!overlapsAny(start, end)) {
      const dialogueText = m[3]?.trim();
      const speakerName = m[1]?.trim() || "";
      // Skip if this match is on a bullet/list line
      const onList = isOnListLine(start);
      // Skip if "speaker" contains numbers/parenthetical quantities (e.g. "Honey jars (5)")
      const isItemName = /\(\d+\)|\bx\d+\b|\b\d+\b/.test(speakerName);
      if (dialogueText && dialogueText.length > 5 && !onList && !isItemName && !isSystemSpeaker(speakerName)) {
        matches.push({
          start,
          end,
          type: "dialogue",
          speaker: speakerName,
          emotion: m[2]?.trim() || undefined,
          text: dialogueText,
        });
      }
    }
  }

  // Action/stage-direction patterns (only if no overlap)
  ACTION_RE.lastIndex = 0;
  while ((m = ACTION_RE.exec(content)) !== null) {
    const rawStart = m.index;
    const matchText = m[0];
    const leadingWS = matchText.length - matchText.trimStart().length;
    const start = rawStart + leadingWS;
    const end = rawStart + matchText.length;
    if (!overlapsAny(start, end)) {
      matches.push({
        start: rawStart,
        end,
        type: "action",
        text: m[1].trim(),
      });
    }
  }

  if (matches.length === 0) {
    return splitNotesFromNarration([{ type: "narration", text: content }]);
  }

  // Sort by position
  matches.sort((a, b) => a.start - b.start);

  // Build raw segments preserving order
  const rawSegments: NarratorSegment[] = [];
  let cursor = 0;
  let lastSpeaker: string | undefined;

  for (const match of matches) {
    if (match.start > cursor) {
      const narr = content.slice(cursor, match.start).trim();
      if (narr) {
        rawSegments.push({ type: "narration", text: narr });
        lastSpeaker = undefined;
      }
    }

    if (match.type === "dialogue") {
      rawSegments.push({
        type: "dialogue",
        speaker: match.speaker,
        text: match.text,
        emotion: match.emotion,
      });
      lastSpeaker = match.speaker;
    } else if (match.type === "inner_voice") {
      rawSegments.push({ type: "inner_voice", text: match.text });
    } else if (match.type === "action") {
      rawSegments.push({
        type: "action",
        text: match.text,
        speaker: lastSpeaker,
      });
    }

    cursor = match.end;
  }

  // Trailing narration
  if (cursor < content.length) {
    const narr = content.slice(cursor).trim();
    if (narr) rawSegments.push({ type: "narration", text: narr });
  }

  // Post-processing: merge consecutive same-speaker dialogue
  const merged: NarratorSegment[] = [];
  for (const seg of rawSegments) {
    if (seg.type === "dialogue" && merged.length > 0) {
      const prev = merged[merged.length - 1];
      if (prev.type === "dialogue" && prev.speaker === seg.speaker) {
        prev.text += "\n\n" + seg.text;
        if (seg.emotion) prev.emotion = seg.emotion;
        continue;
      }
    }
    merged.push({ ...seg });
  }

  // Extract note/list blocks from narration segments
  const withNotes = splitNotesFromNarration(merged.length > 0 ? merged : [{ type: "narration", text: content }]);

  // Append offscreen lines as a world-activity note if any leaked into content
  if (offscreenLines.length > 0) {
    withNotes.push({
      type: "note",
      text: offscreenLines.map(l => `- ${l}`).join("\n"),
      noteTitle: "World Activity",
    });
  }

  return withNotes;
}

// ─── Note / Bullet-List Extraction ───

/**
 * Detects bullet lists, numbered lists, and note-like blocks within narration segments
 * and splits them into dedicated "note" segments with special rendering.
 */
function splitNotesFromNarration(segments: NarratorSegment[]): NarratorSegment[] {
  const result: NarratorSegment[] = [];
  // Matches lines starting with bullets, dashes, numbers, or special markers
  const LIST_LINE = /^(?:\s*[-•*→▸▹▶]\s+|\s*\d+[.)]\s+|\s*[a-z][.)]\s+)/;
  // Header-like lines before lists (short, ends with colon or is bold)
  const NOTE_HEADER = /^(?:\*\*[^*]+\*\*:?|[A-Z][^.!?]{3,60}:)\s*$/;

  for (const seg of segments) {
    if (seg.type !== "narration") {
      result.push(seg);
      continue;
    }

    const lines = seg.text.split("\n");
    let buffer: string[] = [];
    let noteLines: string[] = [];
    let noteTitle: string | undefined;
    let inList = false;

    const flushNote = () => {
      if (noteLines.length > 0) {
        result.push({
          type: "note",
          text: noteLines.join("\n"),
          noteTitle: noteTitle,
        });
        noteLines = [];
        noteTitle = undefined;
      }
    };

    const flushNarration = () => {
      const text = buffer.join("\n").trim();
      if (text) result.push({ type: "narration", text });
      buffer = [];
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isListLine = LIST_LINE.test(line);
      const isHeader = NOTE_HEADER.test(line.trim());

      if (isListLine) {
        if (!inList) {
          // Check if previous line was a header for this list
          if (buffer.length > 0 && NOTE_HEADER.test(buffer[buffer.length - 1].trim())) {
            noteTitle = buffer.pop()!.trim().replace(/^\*\*|\*\*:?$/g, "").replace(/:$/, "");
            flushNarration();
          } else {
            flushNarration();
          }
          inList = true;
        }
        noteLines.push(line);
      } else if (inList && line.trim() === "") {
        // Blank line might end the list or be spacing within
        const nextLine = lines[i + 1];
        if (nextLine && LIST_LINE.test(nextLine)) {
          noteLines.push(line); // spacing within list
        } else {
          flushNote();
          inList = false;
        }
      } else {
        if (inList) {
          flushNote();
          inList = false;
        }
        buffer.push(line);
      }
    }

    // Flush remaining
    if (inList) flushNote();
    flushNarration();
  }

  return result;
}
